'use client';

import React from 'react';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Text } from '../text';
import { Icon } from '../icon';
import { LoadingState } from '../loading-state';

export interface KpiCardProps extends BoxProps {
  /** KPI title */
  title: string;
  /** Current value */
  value: string | number;
  /** Value prefix (e.g., '$', '€') */
  prefix?: string;
  /** Value suffix (e.g., '%', 'k') */
  suffix?: string;
  /** Change percentage */
  change?: number;
  /** Change period description */
  changePeriod?: string;
  /** Trend direction */
  trend?: 'up' | 'down' | 'neutral';
  /** Whether positive change is good (default: true) */
  positiveIsGood?: boolean;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Sparkline data */
  sparklineData?: Array<{ value: number; label?: string }>;
  /** Sparkline color */
  sparklineColor?: string;
  /** Loading state */
  loading?: boolean;
  /** Card variant */
  variant?: 'default' | 'compact' | 'detailed';
}

/**
 * KpiCard component - displays key performance indicators with trends
 * Supports sparklines, change indicators, and various layouts
 */
export const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  prefix,
  suffix,
  change,
  changePeriod = 'vs last period',
  trend,
  positiveIsGood = true,
  icon,
  sparklineData,
  sparklineColor: _sparklineColor,
  loading = false,
  variant = 'default',
  ...boxProps
}) => {
  // Determine trend from change if not explicitly provided
  const actualTrend =
    trend ||
    (change !== undefined ? (change > 0 ? 'up' : change < 0 ? 'down' : 'neutral') : undefined);

  // Determine color based on trend and positiveIsGood
  const getTrendColor = () => {
    if (actualTrend === 'neutral') return 'fg.muted';

    const isPositive = actualTrend === 'up';
    const isGood = positiveIsGood ? isPositive : !isPositive;

    return isGood ? 'fg.success' : 'fg.error';
  };

  const trendColor = getTrendColor();

  // Get trend icon
  const getTrendIcon = () => {
    switch (actualTrend) {
      case 'up':
        return ArrowUpIcon;
      case 'down':
        return ArrowDownIcon;
      default:
        return MinusIcon;
    }
  };

  const TrendIcon = getTrendIcon();

  return (
    <Box
      position="relative"
      padding={variant === 'compact' ? 3 : 5}
      backgroundColor="bg.surface"
      borderRadius="lg"
      border="1px solid"
      borderColor="border.muted"
      transition="all 0.2s"
      _hover={{
        borderColor: 'border.default',
        boxShadow: 'sm',
      }}
      {...boxProps}
    >
      {loading && <LoadingState preset="kpiCard" asOverlay blurBackground />}

      {/* Header with icon and title */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        marginBottom={variant === 'compact' ? 2 : 3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          {icon && (
            <Box color="fg.muted" fontSize="lg">
              {icon}
            </Box>
          )}
          <Text fontSize={variant === 'compact' ? 'sm' : 'md'} color="fg.muted" fontWeight="medium">
            {title}
          </Text>
        </Box>
      </Box>

      {/* Value display */}
      <Box
        display="flex"
        alignItems="baseline"
        gap={1}
        marginBottom={variant === 'detailed' ? 3 : 2}
      >
        {prefix && (
          <Text
            fontSize={variant === 'compact' ? 'lg' : '2xl'}
            fontWeight="semibold"
            color="fg.muted"
          >
            {prefix}
          </Text>
        )}
        <Text fontSize={variant === 'compact' ? 'xl' : '3xl'} fontWeight="bold" color="fg.default">
          {value}
        </Text>
        {suffix && (
          <Text
            fontSize={variant === 'compact' ? 'lg' : '2xl'}
            fontWeight="semibold"
            color="fg.muted"
          >
            {suffix}
          </Text>
        )}
      </Box>

      {/* Change indicator */}
      {change !== undefined && (
        <Box display="flex" alignItems="center" gap={1} marginBottom={sparklineData ? 3 : 0}>
          <Icon color={trendColor}>
            <TrendIcon />
          </Icon>
          <Text
            fontSize={variant === 'compact' ? 'xs' : 'sm'}
            color={trendColor}
            fontWeight="medium"
          >
            {Math.abs(change)}%
          </Text>
          <Text fontSize={variant === 'compact' ? 'xs' : 'sm'} color="fg.muted">
            {changePeriod}
          </Text>
        </Box>
      )}

      {/* Sparkline placeholder — use DevExtreme Sparkline widget directly for sparkline visualizations */}
    </Box>
  );
};

export default KpiCard;
